/**
 * Discussion Service - Manages deliverable discussion threads
 * Handles messages, reactions, mentions, and real-time updates
 */

import { db, auth } from '@/js/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  startAfter
} from 'firebase/firestore';
import { uploadRawFile } from './bunnyUploadService';
import { BUNNY_UPLOAD_CONTEXT, BUNNY_APP_NAMESPACE } from '@/utils/bunnyConstants';
import { logger } from './loggerService';

class DiscussionService {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Upload an image for a discussion message
   * @param {string} deliverableId - The deliverable ID
   * @param {File} file - The image file to upload
   * @returns {Promise<Object>} Attachment metadata with download URL
   */
  async uploadDiscussionImage(deliverableId, file) {
    try {
      logger.info('[DiscussionService] Uploading discussion image', {
        deliverableId,
        name: file.name,
        size: file.size
      });

      const result = await uploadRawFile({
        file,
        context: {
          type: BUNNY_UPLOAD_CONTEXT.TEMP_ATTACHMENT,
          namespace: BUNNY_APP_NAMESPACE.DELIVERABLE_DISCUSSION,
          contextId: deliverableId,
        },
      });

      logger.info('[DiscussionService] Discussion image uploaded', { url: result.url });

      return {
        type: 'image',
        url: result.url,
        storagePath: result.path,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      };
    } catch (error) {
      logger.error('[DiscussionService] Failed to upload discussion image', {
        error: error.message,
        deliverableId
      });
      throw error;
    }
  }

  /**
   * Add a new message to a deliverable discussion
   * @param {string} deliverableId - The deliverable ID
   * @param {string} message - The message text
   * @param {Array} mentions - Array of mentioned user IDs
   * @param {Array} attachments - Array of attachment objects
   * @returns {Promise<Object>} The created message
   */
  async addMessage(deliverableId, message, mentions = [], attachments = []) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const messagesRef = collection(db, 'deliverables', deliverableId, 'discussions');

      // Get user's full name from Firestore users collection
      let fullName = user.displayName || user.email;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.name) {
            fullName = userData.name;
          } else if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          }
        }
      } catch (err) {
        logger.warn('[DiscussionService] Could not fetch user name', { error: err.message });
      }

      const messageData = {
        message: message.trim(),
        userId: user.uid,
        userName: fullName,
        userEmail: user.email,
        timestamp: serverTimestamp(),
        edited: false,
        editedAt: null,
        deleted: false,
        deletedAt: null,
        reactions: {},
        mentions: mentions || [],
        attachments: attachments || [],
        readBy: {} // Track who has read this message
      };

      const docRef = await addDoc(messagesRef, messageData);

      logger.info('[DiscussionService] Message added', {
        deliverableId,
        messageId: docRef.id
      });

      return { id: docRef.id, ...messageData };
    } catch (error) {
      logger.error('[DiscussionService] Failed to add message', {
        error: error.message,
        deliverableId
      });
      throw error;
    }
  }

  /**
   * Get messages for a deliverable
   * @param {string} deliverableId - The deliverable ID
   * @param {number} limitCount - Number of messages to fetch
   * @param {Object} lastDoc - Last document for pagination
   * @returns {Promise<Array>} Array of messages
   */
  async getMessages(deliverableId, limitCount = 50, lastDoc = null) {
    try {
      const messagesRef = collection(db, 'deliverables', deliverableId, 'discussions');

      let q = query(
        messagesRef,
        orderBy('timestamp', 'asc'),
        limit(limitCount)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const messages = [];

      snapshot.forEach(doc => {
        messages.push({
          id: doc.id,
          ...doc.data()
        });
      });

      logger.info('[DiscussionService] Messages fetched', {
        deliverableId,
        count: messages.length
      });

      return messages;
    } catch (error) {
      logger.error('[DiscussionService] Failed to get messages', {
        error: error.message,
        deliverableId
      });
      throw error;
    }
  }

  /**
   * Subscribe to real-time message updates
   * @param {string} deliverableId - The deliverable ID
   * @param {Function} callback - Callback function for updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToMessages(deliverableId, callback) {
    try {
      // Unsubscribe from existing listener if any
      if (this.listeners.has(deliverableId)) {
        const existingUnsubscribe = this.listeners.get(deliverableId);
        existingUnsubscribe();
      }

      const messagesRef = collection(db, 'deliverables', deliverableId, 'discussions');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages = [];
        snapshot.forEach(doc => {
          messages.push({
            id: doc.id,
            ...doc.data()
          });
        });

        callback(messages);
      }, (error) => {
        logger.error('[DiscussionService] Real-time subscription error', {
          error: error.message,
          deliverableId
        });
      });

      this.listeners.set(deliverableId, unsubscribe);

      logger.info('[DiscussionService] Subscribed to messages', {
        deliverableId
      });

      return unsubscribe;
    } catch (error) {
      logger.error('[DiscussionService] Failed to subscribe to messages', {
        error: error.message,
        deliverableId
      });
      throw error;
    }
  }

  /**
   * Edit a message
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @param {string} newMessage - The new message text
   * @param {Array} mentions - Updated array of mentioned user IDs
   * @returns {Promise<void>}
   */
  async editMessage(deliverableId, messageId, newMessage, mentions = [], attachments = null) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const messageRef = doc(db, 'deliverables', deliverableId, 'discussions', messageId);

      // Verify the user owns this message
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();
      if (messageData.userId !== user.uid) {
        throw new Error('You can only edit your own messages');
      }

      const updatePayload = {
        message: newMessage.trim(),
        edited: true,
        editedAt: serverTimestamp(),
        mentions: mentions || []
      };

      // Only update attachments when explicitly provided (not null)
      if (attachments !== null) {
        updatePayload.attachments = attachments;
      }

      await updateDoc(messageRef, updatePayload);

      logger.info('[DiscussionService] Message edited', {
        deliverableId,
        messageId
      });
    } catch (error) {
      logger.error('[DiscussionService] Failed to edit message', {
        error: error.message,
        deliverableId,
        messageId
      });
      throw error;
    }
  }

  /**
   * Delete a message (soft delete)
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @returns {Promise<void>}
   */
  async deleteMessage(deliverableId, messageId) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const messageRef = doc(db, 'deliverables', deliverableId, 'discussions', messageId);

      // Verify the user owns this message
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();
      if (messageData.userId !== user.uid) {
        throw new Error('You can only delete your own messages');
      }

      // Soft delete - keep the message but mark as deleted
      await updateDoc(messageRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        message: '[Message deleted]'
      });

      logger.info('[DiscussionService] Message deleted', {
        deliverableId,
        messageId
      });
    } catch (error) {
      logger.error('[DiscussionService] Failed to delete message', {
        error: error.message,
        deliverableId,
        messageId
      });
      throw error;
    }
  }

  /**
   * Toggle a reaction on a message (supports multiple reactions per user)
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @param {string} emoji - The emoji reaction
   * @returns {Promise<void>}
   */
  async toggleReaction(deliverableId, messageId, emoji) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const messageRef = doc(db, 'deliverables', deliverableId, 'discussions', messageId);

      // Get current reactions
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();
      const reactions = messageData.reactions || {};

      // Create a unique key for each user-emoji combination
      const reactionKey = `${user.uid}_${emoji}`;

      if (reactions[reactionKey]) {
        // Remove if it already exists
        delete reactions[reactionKey];
        logger.info('[DiscussionService] Reaction removed', {
          deliverableId,
          messageId,
          emoji
        });
      } else {
        // Get user's full name
        let fullName = user.displayName || user.email;
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.name) {
              fullName = userData.name;
            } else if (userData.firstName && userData.lastName) {
              fullName = `${userData.firstName} ${userData.lastName}`;
            } else if (userData.firstName) {
              fullName = userData.firstName;
            }
          }
        } catch {
          // Fallback to displayName or email
        }

        // Add the reaction
        reactions[reactionKey] = {
          emoji,
          userId: user.uid,
          userName: fullName,
          timestamp: Date.now()
        };
        logger.info('[DiscussionService] Reaction added', {
          deliverableId,
          messageId,
          emoji
        });
      }

      await updateDoc(messageRef, {
        reactions
      });
    } catch (error) {
      logger.error('[DiscussionService] Failed to toggle reaction', {
        error: error.message,
        deliverableId,
        messageId
      });
      throw error;
    }
  }

  /**
   * Add or update a reaction to a message (legacy method for compatibility)
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @param {string} emoji - The emoji reaction
   * @returns {Promise<void>}
   */
  async addReaction(deliverableId, messageId, emoji) {
    return this.toggleReaction(deliverableId, messageId, emoji);
  }

  /**
   * Remove a reaction from a message (legacy method for compatibility)
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @param {string} emoji - The emoji reaction
   * @returns {Promise<void>}
   */
  async removeReaction(deliverableId, messageId, emoji) {
    return this.toggleReaction(deliverableId, messageId, emoji);
  }

  /**
   * Mark messages as read by the current user
   * @param {string} deliverableId - The deliverable ID
   * @param {Array} messageIds - Array of message IDs to mark as read
   * @returns {Promise<void>}
   */
  async markMessagesAsRead(deliverableId, messageIds) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Get user profile for avatar and full name
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data() || {};
      const userAvatar = userData.profile?.avatar || userData.avatar || null;

      // Get user's full name
      let fullName = user.displayName || user.email;
      if (userData.name) {
        fullName = userData.name;
      } else if (userData.firstName && userData.lastName) {
        fullName = `${userData.firstName} ${userData.lastName}`;
      } else if (userData.firstName) {
        fullName = userData.firstName;
      }

      // Batch update all messages
      const promises = messageIds.map(async (messageId) => {
        const messageRef = doc(db, 'deliverables', deliverableId, 'discussions', messageId);

        // Get the current message to check if it's already read
        const messageDoc = await getDoc(messageRef);
        if (!messageDoc.exists()) return;

        const messageData = messageDoc.data();

        // Don't mark own messages as read
        if (messageData.userId === user.uid) return;

        // Check if already marked as read
        if (messageData.readBy && messageData.readBy[user.uid]) return;

        // Update the readBy field
        await updateDoc(messageRef, {
          [`readBy.${user.uid}`]: {
            timestamp: serverTimestamp(),
            userName: fullName,
            userAvatar: userAvatar
          }
        });
      });

      await Promise.all(promises);

      logger.info('[DiscussionService] Messages marked as read', {
        deliverableId,
        count: messageIds.length
      });
    } catch (error) {
      logger.error('[DiscussionService] Failed to mark messages as read', {
        error: error.message,
        deliverableId
      });
      // Don't throw error to avoid interrupting the user experience
    }
  }

  /**
   * Get read status for a specific message
   * @param {Object} message - The message object
   * @returns {Array} Array of users who have read the message
   */
  getReadByUsers(message) {
    if (!message.readBy || Object.keys(message.readBy).length === 0) {
      return [];
    }

    return Object.entries(message.readBy).map(([userId, data]) => ({
      userId,
      userName: data.userName,
      userAvatar: data.userAvatar,
      timestamp: data.timestamp
    }));
  }

  /**
   * Parse mentions from message text
   * @param {string} text - The message text
   * @returns {Array} Array of mentioned usernames (display names with spaces)
   */
  parseMentions(text) {
    // Updated regex to handle underscores in mentions (for full names)
    const mentionRegex = /@([\w_]+)/g;
    const mentions = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Convert underscores back to spaces for storage
      const username = match[1].replace(/_/g, ' ');
      mentions.push(username);
    }

    return [...new Set(mentions)]; // Remove duplicates
  }

  /**
   * Resolve mentioned usernames to user IDs
   * @param {string} text - The message text
   * @param {Array} availableUsers - Array of user objects with id, name, and email
   * @returns {Array} Array of user IDs for mentioned users
   */
  resolveMentionsToIds(text, availableUsers) {
    const mentionedNames = this.parseMentions(text);
    const userIds = [];

    for (const mentionedName of mentionedNames) {
      // Find user by name (case-insensitive)
      const user = availableUsers.find(u => {
        const userName = (u.name || '').toLowerCase();
        const userEmail = (u.email || '').split('@')[0].toLowerCase();
        const searchName = mentionedName.toLowerCase();
        return userName === searchName || userEmail === searchName;
      });

      if (user) {
        userIds.push(user.id);
      }
    }

    return [...new Set(userIds)]; // Remove duplicates
  }

  /**
   * Update checklist state in a message (toggle checkbox)
   * Does not mark the message as edited or check ownership
   * @param {string} deliverableId - The deliverable ID
   * @param {string} messageId - The message ID
   * @param {string} updatedMessage - The updated message HTML
   * @returns {Promise<void>}
   */
  async updateChecklistState(deliverableId, messageId, updatedMessage) {
    try {
      const messageRef = doc(db, 'deliverables', deliverableId, 'discussions', messageId);
      await updateDoc(messageRef, { message: updatedMessage });

      logger.info('[DiscussionService] Checklist state updated', {
        deliverableId,
        messageId
      });
    } catch (error) {
      logger.error('[DiscussionService] Failed to update checklist state', {
        error: error.message,
        deliverableId,
        messageId
      });
      throw error;
    }
  }

  /**
   * Unsubscribe from all listeners
   */
  unsubscribeAll() {
    this.listeners.forEach((unsubscribe, deliverableId) => {
      logger.info('[DiscussionService] Unsubscribing from messages', {
        deliverableId
      });
      unsubscribe();
    });
    this.listeners.clear();
  }
}

// Export singleton instance
export const discussionService = new DiscussionService();